import { useBrands } from '../../context/BrandsContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import type { Product } from '../../data/products';

function ProductCard({ product, brandSlug }: { product: Product; brandSlug: string }) {
  const navigate = useNavigate();
  const imgs = product.images && product.images.length > 1 ? product.images : [product.image];
  const [idx, setIdx] = useState(0);

  const prev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx(i => (i - 1 + imgs.length) % imgs.length);
  };
  const next = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIdx(i => (i + 1) % imgs.length);
  };

  return (
    <div
      onClick={() => navigate(`/shop/${brandSlug}/${product.id}`)}
      className="group bg-white border border-gray-200 hover:border-black transition-all duration-300 flex flex-col cursor-pointer"
    >
      {/* Image / Carousel */}
      <div className="relative overflow-hidden bg-gray-50 h-64">
        <img
          src={imgs[idx]}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />

        {/* Category badge */}
        <div className="absolute top-4 left-4">
          <span className="bg-black text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
            {product.category}
          </span>
        </div>

        {/* Carousel arrows — only if multiple images */}
        {imgs.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/70 hover:bg-black text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {imgs.map((_, i) => (
                <button
                  key={i}
                  onClick={e => { e.stopPropagation(); setIdx(i); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${i === idx ? 'bg-white w-4' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <span className="text-white text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all bg-black px-5 py-2.5">
            View Product →
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-black text-lg font-black uppercase tracking-tight mb-2">{product.name}</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-1 line-clamp-2">{product.description}</p>
        <div className="flex items-end justify-between pt-4 border-t border-gray-100">
          <div>
            <div className="text-2xl font-black text-black">{product.price}</div>
            {product.priceNote && (
              <div className="text-xs text-gray-400 mt-0.5">{product.priceNote}</div>
            )}
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400 group-hover:text-black transition-colors flex items-center gap-1.5">
            Details
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform inline-block"></i>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ShopPage() {
  const { brand: brandSlug } = useParams<{ brand?: string }>();
  const navigate = useNavigate();
  const { brands, loading } = useBrands();

  const currentBrand = brands.length
    ? (brandSlug ? brands.find(b => b.slug === brandSlug) || brands[0] : brands[0])
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [brandSlug]);

  if (loading || !currentBrand) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-gray-400 text-sm uppercase tracking-widest">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="bg-black pt-28 md:pt-44 pb-10 md:pb-14">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-white/30"></div>
            <span className="text-gray-500 text-xs uppercase tracking-[4px] font-bold">Budzevina Carbon</span>
          </div>
          <h1 className="text-white text-6xl sm:text-8xl md:text-[9rem] font-black tracking-tighter leading-none mb-4">
            SHOP
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-lg">
            Precision dry carbon components. Select your model below.
          </p>
        </div>
      </section>

      {/* Sticky Brand Tabs */}
      <div className="bg-black border-b border-white/10 sticky top-0 md:top-10 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="flex items-center overflow-x-auto scrollbar-none">
            {brands.map((brand) => (
              <button
                key={brand.slug}
                onClick={() => navigate(`/shop/${brand.slug}`)}
                className={`px-5 py-4 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all flex-shrink-0 cursor-pointer border-b-2 ${
                  currentBrand.slug === brand.slug
                    ? 'text-white border-white'
                    : 'text-gray-500 hover:text-gray-300 border-transparent'
                }`}
              >
                {brand.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section className="bg-white py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">

          {/* Brand header */}
          <div className="mb-10 md:mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="text-gray-400 text-xs uppercase tracking-[3px] font-bold mb-2">{currentBrand.fullName}</div>
              <h2 className="text-black text-4xl md:text-6xl font-black tracking-tighter leading-none">
                {currentBrand.name.toUpperCase()}
              </h2>
            </div>
            <p className="text-gray-500 text-sm md:text-base max-w-sm">{currentBrand.tagline}</p>
          </div>

          {/* Compatible Models Bar */}
          <div className="flex items-center gap-3 flex-wrap mb-10 md:mb-16 pb-8 border-b border-gray-100">
            <span className="text-gray-400 text-xs uppercase tracking-[3px] font-bold whitespace-nowrap">Compatible:</span>
            {currentBrand.models.map((model, i) => (
              <span key={i} className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 border-l-2 border-black">
                {model}
              </span>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {currentBrand.products.map((product) => (
              <ProductCard key={product.id} product={product} brandSlug={currentBrand.slug} />
            ))}
          </div>

          {/* Custom CTA */}
          <div className="mt-16 md:mt-24 bg-black p-8 md:p-14 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[2px] bg-white/30"></div>
              <span className="text-gray-500 text-xs uppercase tracking-[4px] font-bold">Custom Orders</span>
              <div className="w-8 h-[2px] bg-white/30"></div>
            </div>
            <h3 className="text-white text-3xl md:text-5xl font-black uppercase tracking-tight mb-4">
              Need Something Custom?
            </h3>
            <p className="text-gray-400 text-base md:text-lg mb-8 max-w-xl mx-auto">
              We manufacture bespoke carbon fiber components. Send us your requirements and we'll build it.
            </p>
            <button
              onClick={() => navigate('/order')}
              className="bg-white text-black px-10 py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-200 transition-all inline-flex items-center gap-3 cursor-pointer"
            >
              Request Custom Quote
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
